import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';

// Contractor-facing form to request supplies, with an optional photo.
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function RequestSupplies() {
  const [items, setItems] = useState('');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { items: items.trim(), notes: notes.trim() || null };
      if (photo) {
        payload.photoBase64 = await fileToBase64(photo);
        payload.photoMime = photo.type || 'image/jpeg';
      }
      return api.createSupplyRequest(payload);
    },
    onSuccess: () => {
      setItems('');
      setNotes('');
      setPhoto(null);
      setDone(true);
    },
  });

  const label: React.CSSProperties = { display: 'block', fontWeight: 600, marginBottom: 4, marginTop: 'var(--spacing-3, 16px)' };
  const input: React.CSSProperties = { width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--color-border, #ccc)', fontFamily: 'inherit', fontSize: 14, boxSizing: 'border-box' };

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: 'var(--spacing-3, 16px)' }}>
      <h2 style={{ marginTop: 0 }}>Request supplies</h2>
      <p style={{ color: 'var(--color-text-muted, #666)' }}>
        Tell the office what you need. Attach a photo if it helps (optional).
      </p>

      {done && (
        <div style={{ background: 'var(--color-teal-25, #D6EBE5)', color: 'var(--color-dark-green, #073A3D)', padding: 12, borderRadius: 8, marginBottom: 12 }}>
          ✓ Request sent. The office has been notified.{' '}
          <button onClick={() => setDone(false)} style={{ background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', color: 'inherit' }}>
            Request more
          </button>
        </div>
      )}

      {!done && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (items.trim()) mutation.mutate();
          }}
        >
          <label style={label}>What do you need? *</label>
          <textarea
            value={items}
            onChange={(e) => setItems(e.target.value)}
            required
            rows={4}
            placeholder="e.g. 2x microfibre cloths, 1L glass cleaner, replacement squeegee"
            style={input}
          />

          <label style={label}>Notes (optional)</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={input} />

          <label style={label}>Photo (optional)</label>
          <input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0] || null)} style={{ ...input, padding: 6 }} />
          {photo && <div style={{ fontSize: 12, color: 'var(--color-text-muted, #666)', marginTop: 4 }}>{photo.name}</div>}

          {mutation.isError && (
            <div style={{ color: 'var(--color-error, #EE2937)', marginTop: 12 }}>
              Could not send the request. Please try again.
            </div>
          )}

          <button
            type="submit"
            disabled={!items.trim() || mutation.isPending}
            style={{
              marginTop: 'var(--spacing-3, 16px)',
              padding: '10px 18px',
              background: 'var(--color-teal, #00B097)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontWeight: 600,
              cursor: items.trim() && !mutation.isPending ? 'pointer' : 'not-allowed',
              opacity: items.trim() && !mutation.isPending ? 1 : 0.6,
            }}
          >
            {mutation.isPending ? 'Sending…' : 'Send request'}
          </button>
        </form>
      )}
    </div>
  );
}
